// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { CATEGORIES, Category } from '../data';
import { 
  Network, Cpu, Search, RefreshCw, ZoomIn, ZoomOut, Maximize2, Zap, 
  Activity, Star, Layers, ShieldCheck, CircleAlert, CheckCircle2 
} from 'lucide-react';

interface VisNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  depth: number;
  parentId?: string;
  category?: number;
  x?: number;
  y?: number;
}

interface VisLink {
  source: string;
  target: string;
}

export const NexoraXD3Visualizer: React.FC = () => {
  const [activeLayout, setActiveLayout] = useState<'radial' | 'treemap' | 'force'>('radial');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null); // null means all
  const [selectedItem, setSelectedItem] = useState<{ name: string; categoryTitle: string; id: number; protocol?: string } | null>(null);
  const [pulseActive, setPulseActive] = useState<boolean>(false);
  
  // Dimensions
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });

  // Filter Categories to Nodes 91 - 100
  const nodes91to100 = useMemo<Category[]>(() => {
    return CATEGORIES.filter(c => c.id >= 91 && c.id <= 100);
  }, []);

  // Category Theme Mapping
  const categoryThemes = useMemo(() => {
    return {
      91: { label: "NexoraX Incubator", color: "#f59e0b", border: "border-amber-500/30", bg: "bg-amber-950/20", glow: "rgba(245,158,11,0.3)" },
      92: { label: "Quantum Crypto", color: "#d946ef", border: "border-fuchsia-500/30", bg: "bg-fuchsia-950/20", glow: "rgba(217,70,239,0.3)" },
      93: { label: "Cognitive Defense", color: "#ef4444", border: "border-red-500/30", bg: "bg-red-950/20", glow: "rgba(239,68,68,0.3)" },
      94: { label: "Directed Neural Comms", color: "#06b6d4", border: "border-cyan-500/30", bg: "bg-cyan-950/20", glow: "rgba(6,182,212,0.3)" },
      95: { label: "UK Gov Regulatory", color: "#10b981", border: "border-emerald-500/30", bg: "bg-emerald-950/20", glow: "rgba(16,185,129,0.3)" },
      96: { label: "SecurePayment Fiscal Swarm", color: "#6366f1", border: "border-indigo-500/30", bg: "bg-indigo-950/20", glow: "rgba(99,102,241,0.3)" },
      97: { label: "Global Governance", color: "#8b5cf6", border: "border-purple-500/30", bg: "bg-purple-950/20", glow: "rgba(139,92,246,0.3)" },
      98: { label: "Health Empirical", color: "#f43f5e", border: "border-rose-500/30", bg: "bg-rose-950/20", glow: "rgba(244,63,94,0.3)" },
      99: { label: "Environmental Vector", color: "#14b8a6", border: "border-teal-500/30", bg: "bg-teal-950/20", glow: "rgba(20,184,166,0.3)" },
      100: { label: "Omega Singularity Node", color: "#38bdf8", border: "border-sky-500/30", bg: "bg-sky-950/20", glow: "rgba(56,189,248,0.3)" }
    } as Record<number, { label: string; color: string; border: string; bg: string; glow: string }>;
  }, []);

  // Filter based on selected category & search
  const hierarchyData = useMemo(() => {
    let filtered = nodes91to100;
    if (selectedCategory !== null) {
      filtered = nodes91to100.filter(c => c.id === selectedCategory);
    }

    return {
      name: "NexoraX Apex",
      children: filtered.map(cat => {
        let subNodes = cat.nodes;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          subNodes = cat.nodes.filter(n => n.toLowerCase().includes(q));
        }
        return {
          id: cat.id,
          name: cat.title,
          protocol: cat.protocol,
          children: subNodes.map(nodeName => ({
            name: nodeName,
            category: cat.id,
            value: 1 // uniform sizes
          }))
        };
      }).filter(c => c.children.length > 0)
    };
  }, [nodes91to100, selectedCategory, searchQuery]);

  // Handle Resize using a nice ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;
    
    let timer: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver((entries) => {
      // Debounce slightly to ensure rendering doesn't flicker
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (entries[0]) {
          const { width } = entries[0].contentRect;
          const calculatedHeight = Math.max(480, Math.min(650, window.innerHeight * 0.6));
          setDimensions({ width: Math.max(300, width), height: calculatedHeight });
        }
      }, 150);
    });

    resizeObserver.observe(containerRef.current);
    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, []);

  // Sync / Pulse animation triggers wave transition
  const triggerPulseWave = () => {
    if (pulseActive) return;
    setPulseActive(true);
    
    // Select all circles in the rendered layouts and pulse them
    const svg = d3.select(svgRef.current);
    
    svg.selectAll('.node-circle')
      .transition()
      .duration(300)
      .attr('r', (d: any) => (d.depth === 2 ? 8 : d.depth === 1 ? 16 : 24))
      .transition()
      .duration(700)
      .ease(d3.easeElastic)
      .attr('r', (d: any) => (d.depth === 2 ? 4 : d.depth === 1 ? 10 : 16))
      .on('end', () => setPulseActive(false));
  };

  // Main D3 Rendering Logic Hook
  useEffect(() => {
    if (!svgRef.current || !dimensions.width) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous layouts

    const { width, height } = dimensions;

    // Outer SVG Styling
    svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("class", "overflow-visible select-none");

    // Setup interactive zoom group
    const gMain = svg.append("g").attr("class", "main-group");

    // Default Zoom and Pan
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        gMain.attr("transform", event.transform);
      });
    
    svg.call(zoom);

    // Initial position reset helper
    const resetZoom = () => {
      svg.transition().duration(750).call(
        zoom.transform,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(0.85)
      );
    };

    // 1. RADIAL CONSTELATION TREE RENDERING
    if (activeLayout === 'radial') {
      const radius = Math.min(width, height) / 2.3;
      
      const rootNode = d3.hierarchy(hierarchyData);
      
      const radialTree = d3.tree()
        .size([2 * Math.PI, radius])
        .separation((a, b) => (a.parent === b.parent ? 1.2 : 2.4) / (a.depth || 1));

      radialTree(rootNode);

      // Centered translation coordinates default
      svg.transition().duration(200).call(
        zoom.transform,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(0.75)
      );

      const projectRadial = (x: number, y: number) => {
        const angle = x - Math.PI / 2;
        return [y * Math.cos(angle), y * Math.sin(angle)];
      };

      // Links Grid Lines
      gMain.append("g")
        .attr("fill", "none")
        .attr("stroke", "rgba(51, 65, 85, 0.4)")
        .attr("stroke-width", 1.2)
        .selectAll("path")
        .data(rootNode.links())
        .join("path")
        .attr("d", d3.linkRadial<any, any>()
          .angle(d => d.x)
          .radius(d => d.y) as any
        );

      // Individual nodes
      const node = gMain.append("g")
        .selectAll("g")
        .data(rootNode.descendants())
        .join("g")
        .attr("transform", d => {
          const [px, py] = projectRadial(d.x, d.y);
          return `translate(${px},${py})`;
        });

      // Circles
      node.append("circle")
        .attr("class", "node-circle cursor-pointer transition-all duration-300 hover:scale-130")
        .attr("r", d => d.depth === 2 ? 4.5 : d.depth === 1 ? 10 : 16)
        .attr("fill", d => {
          if (d.depth === 0) return "#f59e0b"; // Golden center NexoraX Core
          if (d.depth === 1) {
            const catId = (d.data as any).id;
            return categoryThemes[catId]?.color || "#d97706";
          }
          const parentCatId = (d.parent?.data as any).id;
          return categoryThemes[parentCatId]?.color || "#64748b";
        })
        .attr("opacity", d => d.depth === 2 ? 0.75 : 0.95)
        .attr("stroke", d => {
          if (d.depth === 0) return "rgba(255, 255, 255, 0.3)";
          return "transparent";
        })
        .attr("stroke-width", 2)
        .on("click", (event, d) => {
          event.stopPropagation();
          if (d.depth === 2) {
            const parentCat = d.parent?.data as any;
            setSelectedItem({
              name: d.data.name,
              categoryTitle: parentCat.name,
              id: parentCat.id,
              protocol: parentCat.protocol
            });
          } else if (d.depth === 1) {
            const catData = d.data as any;
            setSelectedItem({
              name: "Category Summary Overview",
              categoryTitle: catData.name,
              id: catData.id,
              protocol: catData.protocol
            });
          }
        });

      // Subtle pulse rings for Category heads
      node.filter(d => d.depth === 1).append("circle")
        .attr("r", 15)
        .attr("fill", "none")
        .attr("stroke", d => {
          const catId = (d.data as any).id;
          return categoryThemes[catId]?.color || "#f59e0b";
        })
        .attr("stroke-width", 1)
        .attr("opacity", 0.3)
        .attr("class", "animate-ping")
        .style("transform-origin", "0px 0px");

      // Labels pairing
      node.append("text")
        .attr("dy", "0.31em")
        .attr("x", d => (d.x < Math.PI === d.depth > 0 ? 8 : -8))
        .attr("text-anchor", d => (d.x < Math.PI === d.depth > 0 ? "start" : "end"))
        .attr("transform", d => {
          if (d.depth === 0) return "rotate(0)";
          // rotate text slightly to make it aligned with coordinate projection
          const angle = (d.x * 180 / Math.PI - 90);
          return `rotate(${d.x >= Math.PI ? angle + 180 : angle})`;
        })
        .text(d => {
          // If all nodes displayed, truncate subcategory name to keep clear resolution
          if (d.depth === 2) {
            const nameStr = d.data.name || '';
            return nameStr.length > 20 ? nameStr.substring(0, 18) + '...' : nameStr;
          }
          if (d.depth === 1) {
            const labelStr = (d.data as any).name || '';
            return labelStr.split(':')[0] || labelStr;
          }
          return "NEXORAX APEX CORE";
        })
        .attr("fill", d => d.depth === 0 ? "#ffffff" : d.depth === 1 ? "#f1f5f9" : "#94a3b8")
        .attr("font-family", "JetBrains Mono, ui-monospace, sans-serif")
        .attr("font-size", d => d.depth === 0 ? "11px" : d.depth === 1 ? "10px" : "8px")
        .attr("font-weight", d => d.depth <= 1 ? "bold" : "normal")
        .attr("class", "pointer-events-none")
        .attr("opacity", d => {
          if (d.depth <= 1) return 1;
          // Hide leaf node names if they are too densely packed across all categories
          return selectedCategory === null ? 0.25 : 0.85;
        });
    }

    // 2. D3 TREEMAP RENDERING
    if (activeLayout === 'treemap') {
      const rootNode = d3.hierarchy(hierarchyData)
        .sum(d => d.value || 0)
        .sort((a, b) => (b.value || 0) - (a.value || 0));

      const treemapLayout = d3.treemap()
        .size([width, height])
        .paddingOuter(4)
        .paddingInner(1.5);

      treemapLayout(rootNode);

      // Centered translation coordinates default
      svg.transition().duration(200).call(
        zoom.transform,
        d3.zoomIdentity.translate(0, 0).scale(1)
      );

      const themeColors = Object.keys(categoryThemes).map(k => categoryThemes[Number(k)].color);
      const colorScale = d3.scaleOrdinal<string>().range(themeColors);

      const cell = gMain.selectAll("g")
        .data(rootNode.leaves())
        .join("g")
        .attr("transform", d => `translate(${(d as any).x0},${(d as any).y0})`);

      cell.append("rect")
        .attr("width", d => Math.max(0, (d as any).x1 - (d as any).x0))
        .attr("height", d => Math.max(0, (d as any).y1 - (d as any).y0))
        .attr("fill", d => {
          const catId = (d.data as any).category;
          return categoryThemes[catId]?.color || colorScale(catId as any);
        })
        .attr("opacity", 0.4)
        .attr("stroke", d => {
          const catId = d.data.category;
          return categoryThemes[catId]?.color || "transparent";
        })
        .attr("stroke-width", 1)
        .attr("class", "cursor-pointer hover:opacity-80 transition-all duration-150")
        .on("click", (event, d) => {
          event.stopPropagation();
          const parentCat = d.parent?.data as any;
          setSelectedItem({
            name: d.data.name,
            categoryTitle: parentCat?.name || "Matrix Node Context",
            id: parentCat?.id || d.data.category,
            protocol: parentCat?.protocol
          });
        });

      cell.append("text")
        .attr("x", 4)
        .attr("y", 12)
        .text(d => {
          const boxWidth = (d as any).x1 - (d as any).x0;
          if (boxWidth < 45) return ""; // Too small
          const nameStr = d.data.name || '';
          return nameStr.length > 10 ? nameStr.substring(0, 8) + '..' : nameStr;
        })
        .attr("fill", "#ffffff")
        .attr("font-family", "JetBrains Mono, monospace")
        .attr("font-size", "7.5px")
        .attr("opacity", 0.75)
        .attr("class", "pointer-events-none");
    }

    // 3. FORCE-DIRECTED CLUSTER CONSTRAINT MAP
    if (activeLayout === 'force') {
      const nodes: VisNode[] = [{ id: "root", name: "NexoraX Core Hub", depth: 0 }];
      const links: VisLink[] = [];

      svg.transition().duration(200).call(
        zoom.transform,
        d3.zoomIdentity.translate(width / 2, height / 2).scale(0.85)
      );

      // Populate nodes and links recursively
      hierarchyData.children.forEach(cat => {
        const catId = cat.id.toString();
        nodes.push({ 
          id: catId, 
          name: cat.name.split(':')[0], 
          depth: 1, 
          parentId: "root",
          category: cat.id
        });
        links.push({ source: "root", target: catId });

        cat.children.forEach(item => {
          const itemId = `${catId}-${item.name}`;
          nodes.push({ 
            id: itemId, 
            name: item.name, 
            depth: 2, 
            parentId: catId,
            category: cat.id
          });
          links.push({ source: catId, target: itemId });
        });
      });

      // Physics Simulation setups
      const simulation = d3.forceSimulation<VisNode>(nodes)
        .force("link", d3.forceLink<VisNode, VisLink>(links)
          .id(d => d.id)
          .distance(d => {
            // Root links longer, nested child links shorter
            return d.source === "root" ? 140 : 35;
          })
        )
        .force("charge", d3.forceManyBody<VisNode>().strength(d => d.depth === 0 ? -150 : d.depth === 1 ? -60 : -12))
        .force("center", d3.forceCenter(0, 0))
        .force("collide", d3.forceCollide<VisNode>().radius(d => d.depth === 0 ? 30 : d.depth === 1 ? 15 : 6));

      // Append connections line paths
      const strokeLink = gMain.append("g")
        .attr("stroke", "rgba(51, 65, 85, 0.45)")
        .attr("stroke-width", 0.7)
        .selectAll("line")
        .data(links)
        .join("line");

      // Draggable handles
      const drag = d3.drag<any, any>()
        .on("start", (event, d) => {
          if (!event.active) simulation.alphaTarget(0.2).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on("drag", (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on("end", (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        });

      // Render Circle nodes
      const nodeG = gMain.append("g")
        .selectAll("circle")
        .data(nodes)
        .join("circle")
        .attr("class", "node-circle cursor-pointer hover:r-[1.3em] transition-all duration-200")
        .attr("r", d => d.depth === 0 ? 14 : d.depth === 1 ? 9 : 3.5)
        .attr("fill", d => {
          if (d.depth === 0) return "#f59e0b"; // Core
          return categoryThemes[d.category!]?.color || "#64748b";
        })
        .attr("opacity", d => d.depth === 2 ? 0.75 : 0.95)
        .attr("stroke", "rgba(0,0,0,0.5)")
        .attr("stroke-width", 1)
        .call(drag as any)
        .on("click", (event, d) => {
          event.stopPropagation();
          if (d.depth === 2) {
            const correspondingCategoryObj = nodes91to100.find(c => c.id === d.category);
            setSelectedItem({
              name: d.name,
              categoryTitle: correspondingCategoryObj?.title || `Category ${d.category}`,
              id: d.category!,
              protocol: correspondingCategoryObj?.protocol
            });
          } else if (d.depth === 1) {
            const correspondingCategoryObj = nodes91to100.find(c => c.id === d.category);
            setSelectedItem({
              name: "Parent Segment NexoraX Vector",
              categoryTitle: correspondingCategoryObj?.title || d.name,
              id: d.category!,
              protocol: correspondingCategoryObj?.protocol
            });
          }
        });

      // Map positions to ticker
      simulation.on("tick", () => {
        strokeLink
          .attr("x1", d => (d.source as any).x)
          .attr("y1", d => (d.source as any).y)
          .attr("x2", d => (d.target as any).x)
          .attr("y2", d => (d.target as any).y);

        nodeG
          .attr("cx", d => d.x!)
          .attr("cy", d => d.y!);
      });

      // Cleanup simulation thread on unmount
      return () => {
        simulation.stop();
      };
    }

  }, [activeLayout, hierarchyData, dimensions, categoryThemes, nodes91to100, selectedCategory]);

  // Counting sub-categories dynamically
  const leafCountNum = useMemo(() => {
    let sum = 0;
    hierarchyData.children.forEach(c => {
      sum += c.children.length;
    });
    return sum;
  }, [hierarchyData]);

  return (
    <div id="NexoraX-d3-visualizer-panel" className="bg-gradient-to-b from-[#020204] to-black rounded-2xl border border-neutral-800/40 p-4 md:p-6 space-y-6 flex flex-col relative overflow-hidden">
      
      {/* Absolute Header Segment overlay */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10 border-b border-neutral-900 pb-5">
        <div>
          <div className="flex items-center gap-2 text-rose-500 font-mono text-[9px] font-bold uppercase tracking-widest bg-rose-950/20 py-0.5 px-2 border border-rose-500/10 rounded-full w-fit">
            <Activity className="w-3 h-3 text-rose-500 animate-pulse" />
            <span>Active Visual Distribution Terminal</span>
          </div>
          <h3 className="text-md md:text-lg font-sans font-black text-white uppercase tracking-wider mt-2.5">
            100 Sub-Categories Dynamic Mapping Ledger <span className="text-amber-500 font-mono">({leafCountNum} Leaf Streams)</span>
          </h3>
          <p className="text-[10px] text-slate-500 font-mono mt-0.5 uppercase tracking-wide">
            Realtime Node Spanning: Category 91 (NexoraX Incubator) to Category 100 (Omega Singularity)
          </p>
        </div>

        {/* Layout Swapper Buttons */}
        <div className="flex flex-wrap gap-1.5 shrink-0 bg-[#06060c] p-1 rounded-xl border border-neutral-900">
          <button
            onClick={() => setActiveLayout('radial')}
            className={`px-3 py-1.5 rounded-lg text-[9px] font-mono font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
              activeLayout === 'radial'
                ? 'bg-amber-500 text-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-neutral-900/40'
            }`}
          >
            <Network className="w-3 h-3" />
            <span>Radial Tree</span>
          </button>
          
          <button
            onClick={() => setActiveLayout('treemap')}
            className={`px-3 py-1.5 rounded-lg text-[9px] font-mono font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
              activeLayout === 'treemap'
                ? 'bg-amber-500 text-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-neutral-900/40'
            }`}
          >
            <Layers className="w-3 h-3" />
            <span>Matrix Treemap</span>
          </button>

          <button
            onClick={() => setActiveLayout('force')}
            className={`px-3 py-1.5 rounded-lg text-[9px] font-mono font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${
              activeLayout === 'force'
                ? 'bg-amber-500 text-black shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-neutral-900/40'
            }`}
          >
            <Cpu className="w-3 h-3" />
            <span>Force Swarm</span>
          </button>
        </div>
      </div>

      {/* Grid containing Filters Sidebar, Main SVG Canvas, Node Description Inspector details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* Left Column Span 3: Category Filtering list */}
        <div className="lg:col-span-3 flex flex-col space-y-4">
          <div className="bg-[#050508] border border-neutral-900 p-4 rounded-xl space-y-3.5">
            <div className="text-[9px] font-mono font-extrabold text-slate-500 uppercase tracking-widest border-b border-neutral-900 pb-2">
              Filter Branch Select
            </div>

            {/* Input Search Specific */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input 
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Lookup micro-node (e.g. 'SecurePayment', 'Zakat')..."
                className="w-full bg-[#020204] border border-neutral-800 text-slate-300 placeholder-slate-600 pl-9 pr-3 py-2 rounded-lg text-[10px] font-mono tracking-wide focus:border-amber-500/50 focus:outline-none transition-all"
              />
            </div>

            {/* Dynamic Buttons */}
            <div className="flex flex-col gap-1.5 max-h-[260px] overflow-y-auto pr-1">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`p-2 rounded-lg text-left font-mono text-[9px] uppercase tracking-wide font-black transition-all border flex justify-between items-center ${
                  selectedCategory === null
                    ? "border-amber-500 bg-amber-950/20 text-white shadow-sm"
                    : "border-neutral-900 hover:border-neutral-800 text-slate-400 hover:text-white"
                }`}
              >
                <span>🌐 All Grand Ranges (91-100)</span>
                <span className="text-[8px] opacity-65">1000N</span>
              </button>

              {nodes91to100.map(cat => {
                const themeData = categoryThemes[cat.id];
                const activeTheme = selectedCategory === cat.id;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`p-2 rounded-lg text-left font-mono text-[9px] uppercase tracking-wide font-bold transition-all border flex justify-between items-center ${
                      activeTheme
                        ? `text-white bg-neutral-950 shadow-sm`
                        : "border-neutral-900 hover:border-neutral-800 text-slate-400 hover:text-white"
                    }`}
                    style={activeTheme ? { borderColor: themeData.color, boxShadow: `0 0 10px ${themeData.glow}` } : {}}
                  >
                    <span className="truncate pr-2">
                      <span className="font-extrabold pr-1.5" style={{ color: themeData.color }}>NODE-{cat.id}</span>
                      {themeData.label}
                    </span>
                    <span className="text-[7.5px] bg-[#020204] border border-neutral-900 px-1 py-0.5 rounded text-slate-400 font-mono">100N</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#06060c] to-[#04040a] border border-zinc-900 p-4 rounded-xl space-y-3.5">
            <div className="text-[9px] font-mono font-extrabold text-[#f59e0b] uppercase tracking-widest flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
              <span>NexoraXty Pulse Bridge</span>
            </div>
            <p className="text-slate-400 font-sans text-[10px] leading-relaxed">
              Force-realign sub-category clusters and dispatch high-frequency testing signals across the unified matrix routing.
            </p>
            <button
              onClick={triggerPulseWave}
              disabled={pulseActive}
              className="w-full py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:opacity-40 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 shadow-md active:scale-95 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${pulseActive ? 'animate-spin' : ''}`} />
              <span>{pulseActive ? 'Transmitting Pulse' : 'Emit NexoraX Pulse'}</span>
            </button>
          </div>
        </div>

        {/* Center Column Span 6: Svg visual hierarchy canvas */}
        <div className="lg:col-span-6 bg-[#010103] rounded-2xl border border-neutral-900 relative flex flex-col items-center justify-center min-h-[380px]">
          
          <div className="absolute top-4 left-4 z-20 flex gap-2">
            <div className="bg-black/80 border border-neutral-800 rounded-lg px-2.5 py-1 text-[9px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
              <Maximize2 className="w-3 h-3 text-[#f59e0b]" />
              <span>Zoom & Drag Active</span>
            </div>
          </div>

          {/* Canvas wrapping frame with ResizeObserver listener */}
          <div ref={containerRef} className="w-full h-full relative overflow-hidden flex items-center justify-center">
            <svg ref={svgRef} className="w-full h-full text-white pointer-events-auto" />
          </div>
        </div>

        {/* Right Column Span 3: Selected node metadata details */}
        <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
          <div className="bg-[#050508] border border-neutral-900 p-4 rounded-xl flex-1 flex flex-col justify-between min-h-[220px]">
            <div>
              <div className="text-[9px] font-mono font-extrabold text-slate-500 uppercase tracking-widest border-b border-neutral-900 pb-2 mb-3">
                Selected Item Intelligence
              </div>

              {selectedItem ? (
                <div className="space-y-3">
                  <div>
                    <span className="text-[7.5px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider font-extrabold" 
                          style={{ color: categoryThemes[selectedItem.id]?.color, backgroundColor: `${categoryThemes[selectedItem.id]?.color}15`, border: `1px solid ${categoryThemes[selectedItem.id]?.color}33` }}>
                      Node {selectedItem.id} ({categoryThemes[selectedItem.id]?.label})
                    </span>
                    <h4 className="text-xs font-black text-white uppercase tracking-wider font-sans mt-2.5 leading-snug">
                      {selectedItem.name}
                    </h4>
                  </div>

                  <p className="text-[10px] text-zinc-400 font-sans leading-relaxed">
                    This sub-category functions under the official regulatory guideline protocol: {selectedItem.protocol || 'No custom deployment directive registered. Asset operations inherited from NexoraX Gate matrix.'}
                  </p>

                  <div className="p-3 bg-neutral-950 border border-neutral-900 rounded-lg space-y-1 bg-black/60 font-mono text-[8px] uppercase tracking-wide">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Node SLA Goal:</span>
                      <span className="text-amber-500 font-bold">0.8s Heartbeat</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">3% Community Allocation:</span>
                      <span className="text-amber-500 font-bold">1% Perpetual Split</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">SecurePayment Sort:</span>
                      <span className="text-zinc-300">Verified Payment Configuration</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">SecurePayment Account:</span>
                      <span className="text-zinc-300">Secure Billing Provider</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[220px] flex flex-col justify-center items-center text-center text-slate-500 p-4">
                  <Network className="w-8 h-8 text-neutral-800 mb-2" />
                  <div className="text-[9px] font-mono uppercase tracking-widest font-black text-neutral-600">No Item Highlighted</div>
                  <p className="text-[8px] font-mono uppercase tracking-wider text-neutral-700 max-w-[170px] mt-1 text-center">
                    Click any point or segment on the visual map to load its specific operational telemetry ledger and Secure Payment Provider funding structures.
                  </p>
                </div>
              )}
            </div>

            {selectedItem && (
              <div className="pt-4 border-t border-neutral-900 mt-4">
                <a
                  href="https://buy.stripe.com/8x2bIU2t59oYgTtfxQaMU00"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 bg-neutral-900 hover:bg-neutral-850 hover:text-white border border-neutral-800 text-slate-300 font-mono text-[9px] font-bold uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-[0.98]"
                >
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <span>Audit Placement Ledger</span>
                </a>
              </div>
            )}
          </div>

          <div className="p-4 rounded-xl border border-dashed border-neutral-800/60 bg-[#020204]/40 font-mono text-[8px] uppercase tracking-wide text-zinc-500 leading-relaxed text-center space-y-1 select-none">
            <span className="font-bold text-amber-500 block">NexoraX Compliance Directive:</span>
            <span>Mirror status sync logged to Google Drive via CloudSync-02. Standard 1% Levy extracted. 08000 Phone anchor registered.</span>
          </div>
        </div>

      </div>

    </div>
  );
};
